import React, { useState, useCallback } from 'react';

import { supabase } from '../../lib/supabase';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'in-progress': 'bg-blue-100 text-blue-800 border-blue-200',
  scheduled: 'bg-purple-100 text-purple-800 border-purple-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  cancelled: 'bg-gray-100 text-gray-700 border-gray-200',
  'on-hold': 'bg-orange-100 text-orange-800 border-orange-200',
};

const PRIORITY_COLORS = {
  critical: 'text-red-600',
  high: 'text-orange-500',
  medium: 'text-blue-500',
  low: 'text-green-600',
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateTime = (d) => {
  if (!d) return '—';
  return new Date(d)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const TicketCard = ({ ticket, onClick, isSelected }) => (
  <button
    onClick={() => onClick(ticket)}
    className={`w-full text-left p-4 rounded-xl border transition-all ${
      isSelected
        ? 'border-blue-500 bg-blue-50 shadow-md'
        : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
    }`}
  >
    <div className="flex items-start justify-between gap-3 mb-2">
      <div>
        <p className="text-sm font-semibold text-gray-900">{ticket?.ticket_id || ticket?.id?.slice(0, 8)}</p>
        <p className="text-xs text-gray-500 mt-0.5">{formatDate(ticket?.created_at)}</p>
      </div>
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize ${
        STATUS_COLORS?.[ticket?.status?.toLowerCase()] || 'bg-gray-100 text-gray-700 border-gray-200'
      }`}>
        {ticket?.status || 'Pending'}
      </span>
    </div>
    <p className="text-sm text-gray-700 font-medium line-clamp-1">{ticket?.service_type || ticket?.serviceType || 'Service Request'}</p>
    {ticket?.description && (
      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{ticket?.description}</p>
    )}
    <div className="flex items-center gap-3 mt-2">
      {ticket?.priority && (
        <span className={`text-xs font-medium capitalize ${PRIORITY_COLORS?.[ticket?.priority?.toLowerCase()] || 'text-gray-500'}`}>
          {ticket?.priority} Priority
        </span>
      )}
      {ticket?.scheduled_date && (
        <span className="text-xs text-gray-500 flex items-center gap-1">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          {formatDate(ticket?.scheduled_date)}
        </span>
      )}
    </div>
  </button>
);

const TicketDetail = ({ ticket, history, photos, onBack }) => {
  const statusColor = STATUS_COLORS?.[ticket?.status?.toLowerCase()] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <div className="space-y-5">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        Back to tickets
      </button>
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{ticket?.ticket_id || `#${ticket?.id?.slice(0, 8)}`}</h2>
            <p className="text-sm text-gray-500 mt-0.5">Opened {formatDate(ticket?.created_at)}</p>
          </div>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border capitalize ${statusColor}`}>
            {ticket?.status || 'Pending'}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Service Type</p>
            <p className="text-sm font-medium text-gray-900">{ticket?.service_type || ticket?.serviceType || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Priority</p>
            <p className={`text-sm font-medium capitalize ${PRIORITY_COLORS?.[ticket?.priority?.toLowerCase()] || 'text-gray-700'}`}>
              {ticket?.priority || '—'}
            </p>
          </div>
          {ticket?.scheduled_date && (
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Scheduled</p>
              <p className="text-sm font-medium text-gray-900">{formatDateTime(ticket?.scheduled_date)}</p>
            </div>
          )}
          {ticket?.estimated_duration && (
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Est. Duration</p>
              <p className="text-sm font-medium text-gray-900">{ticket?.estimated_duration}</p>
            </div>
          )}
        </div>
        {ticket?.description && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Description</p>
            <p className="text-sm text-gray-700 leading-relaxed">{ticket?.description}</p>
          </div>
        )}
      </div>
      {/* Timeline */}
      {history?.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            Activity Timeline
          </h3>
          <div className="space-y-4">
            {history?.map((event, i) => (
              <div key={event?.id || i} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/></svg>
                  </div>
                  {i < history?.length - 1 && <div className="w-0.5 flex-1 bg-gray-200 mt-1" />}
                </div>
                <div className="flex-1 pb-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900">{event?.action}</p>
                    <p className="text-xs text-gray-400">{formatDateTime(event?.created_at)}</p>
                  </div>
                  {event?.description && (
                    <p className="text-xs text-gray-500 mt-0.5">{event?.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Photos */}
      {photos?.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            Photos ({photos?.length})
          </h3>
          <div className="grid grid-cols-3 gap-2">
            {photos?.map((p) => (
              <a key={p?.id} href={p?.file_url} target="_blank" rel="noopener noreferrer" className="block">
                <div className="aspect-square rounded-lg overflow-hidden border border-gray-200 hover:opacity-80 transition-opacity">
                  <img src={p?.file_url} alt={p?.caption || 'Ticket photo'} className="w-full h-full object-cover" />
                </div>
                {p?.caption && <p className="text-xs text-gray-500 mt-1 line-clamp-1">{p?.caption}</p>}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const CustomerTicketPortal = () => {
  const [email, setEmail] = useState('');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketHistory, setTicketHistory] = useState([]);
  const [ticketPhotos, setTicketPhotos] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  const handleSearch = useCallback(async (e) => {
    e?.preventDefault();
    if (!email?.trim()) return;
    setLoading(true);
    setError('');
    setSearched(false);
    setSelectedTicket(null);
    setTickets([]);
    try {
      // Find customer by email
      const { data: customers, error: custErr } = await supabase
        ?.from('customers')
        ?.select('id, name, email')
        ?.ilike('email', email?.trim());
      if (custErr) throw custErr;

      if (!customers?.length) {
        setTickets([]);
        setSearched(true);
        setLoading(false);
        return;
      }

      const customerIds = customers?.map(c => c?.id);

      // Fetch tickets for those customers
      const { data: ticketData, error: ticketErr } = await supabase
        ?.from('tickets')
        ?.select(`
          id,
          ticket_id,
          status,
          priority,
          service_type,
          description,
          created_at,
          scheduled_date,
          estimated_duration,
          customer_id
        `)
        ?.in('customer_id', customerIds)
        ?.order('created_at', { ascending: false });
      if (ticketErr) throw ticketErr;

      // Attach customer name
      const customerMap = {};
      customers?.forEach(c => { customerMap[c.id] = c; });
      const enriched = (ticketData || [])?.map(t => ({
        ...t,
        customerName: customerMap?.[t?.customer_id]?.name || '',
      }));

      setTickets(enriched);
      setSearched(true);
    } catch (err) {
      console.error('Portal search error:', err);
      setError('Unable to load tickets. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [email]);

  const handleSelectTicket = useCallback(async (ticket) => {
    setSelectedTicket(ticket);
    setLoadingDetail(true);
    try {
      const [histRes, photoRes] = await Promise.all([
        supabase?.from('ticket_history')?.select('*')?.eq('ticket_id', ticket?.id)?.order('created_at', { ascending: true }),
        supabase?.from('ticket_photos')?.select('*')?.eq('ticket_id', ticket?.id)?.order('created_at', { ascending: false }),
      ]);
      setTicketHistory(histRes?.data || []);
      setTicketPhotos(photoRes?.data || []);
    } catch (err) {
      console.error('Detail load error:', err);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const filteredTickets = filterStatus === 'all'
    ? tickets
    : tickets?.filter(t => t?.status?.toLowerCase() === filterStatus);

  const statusCounts = tickets?.reduce((acc, t) => {
    const s = t?.status?.toLowerCase() || 'pending';
    acc[s] = (acc?.[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Ticket Portal</h1>
              <p className="text-xs text-gray-500">Track your service requests</p>
            </div>
          </div>
          {searched && tickets?.length > 0 && (
            <button
              onClick={() => { setSearched(false); setTickets([]); setSelectedTicket(null); setEmail(''); }}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Search again
            </button>
          )}
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Search Form */}
        {!searched && (
          <div className="max-w-lg mx-auto">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Find Your Tickets</h2>
              <p className="text-gray-500">Enter your email address to view all your service requests and their current status.</p>
            </div>
            <form onSubmit={handleSearch} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 bg-white"
                />
              </div>
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
              <button
                type="submit"
                disabled={loading || !email?.trim()}
                className="w-full py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Searching...</>
                ) : (
                  <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg> View My Tickets</>
                )}
              </button>
            </form>
          </div>
        )}

        {/* Results */}
        {searched && (
          <div>
            {tickets?.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No tickets found</h3>
                <p className="text-gray-500 mb-6">No service tickets were found for <strong>{email}</strong>.</p>
                <button
                  onClick={() => { setSearched(false); setEmail(''); }}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  Try another email
                </button>
              </div>
            ) : selectedTicket ? (
              loadingDetail ? (
                <div className="flex items-center justify-center py-16">
                  <div className="flex items-center gap-3 text-gray-500">
                    <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <span>Loading ticket details...</span>
                  </div>
                </div>
              ) : (
                <TicketDetail
                  ticket={selectedTicket}
                  history={ticketHistory}
                  photos={ticketPhotos}
                  onBack={() => setSelectedTicket(null)}
                />
              )
            ) : (
              <div>
                {/* Stats row */}
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Your Tickets</h2>
                    <p className="text-sm text-gray-500">{tickets?.length} ticket{tickets?.length !== 1 ? 's' : ''} for {email}</p>
                  </div>
                  <div className="flex gap-2 flex-wrap justify-end">
                    {['all', 'pending', 'in-progress', 'scheduled', 'completed']?.map(s => (
                      <button
                        key={s}
                        onClick={() => setFilterStatus(s)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors capitalize ${
                          filterStatus === s
                            ? 'bg-blue-600 text-white' :'bg-white border border-gray-200 text-gray-600 hover:border-blue-300'
                        }`}
                      >
                        {s === 'all' ? `All (${tickets?.length})` : `${s} (${statusCounts?.[s] || 0})`}
                      </button>
                    ))}
                  </div>
                </div>

                {filteredTickets?.length === 0 ? (
                  <div className="text-center py-10 bg-white rounded-2xl border border-gray-200">
                    <p className="text-gray-500">No tickets with status "{filterStatus}"</p>
                  </div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {filteredTickets?.map(ticket => (
                      <TicketCard
                        key={ticket?.id}
                        ticket={ticket}
                        onClick={handleSelectTicket}
                        isSelected={selectedTicket?.id === ticket?.id}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerTicketPortal;
